// agent.js — bc-agent as one script tag:
//
//   <script src="https://agent.bowdencloud.com/agent.js" data-side="right"></script>
//
// Put it in <head>, above the app's own scripts. It works out which site it is on, runs any hook
// the site needs before its app boots, and mounts the chat bar once the page is up. The site's
// adapter and bridge load from a chunk next to this file when the visitor first opens the chat
// (or hovers the launcher). Everything runs in the browser: these are static files, and the model
// is called straight from the page with the visitor's own OpenAI key. See README.md for options.
import { siteById, siteForUrl } from '../sites/registry.js';
import { readConfig } from './config.js';
import { runEarlyHooks } from './early.js';
import { mountWidget } from './ui.js';

/* global __CHUNKS__, __VERSION__ */
const CHUNKS = typeof __CHUNKS__ === 'object' ? __CHUNKS__ : {};
const VERSION = typeof __VERSION__ === 'string' ? __VERSION__ : 'dev';

function findScript() {
  if (document.currentScript) return document.currentScript;
  // type="module" or injected late: currentScript is null
  return document.querySelector('script[data-bc-agent], script[src*="/agent.js"], script[src$="agent.js"]');
}

/**
 * Keys typed in the chat bar must not reach the app (Space plays in MIDI, Delete removes notes…).
 * A listener on window in the capture phase runs before any of the app's own, as long as this
 * script runs first; it hands every key to the chat bar and stops the ones that are the chat's.
 */
function installFirewall() {
  const fw = { host: null, handle: null };
  const onKey = (e) => {
    if (!fw.handle) return;
    const inside = !!fw.host && e.composedPath().includes(fw.host);
    if (fw.handle(e, inside) || inside) e.stopImmediatePropagation();
  };
  for (const t of ['keydown', 'keyup', 'keypress']) window.addEventListener(t, onKey, true);
  // the clipboard too: a paste into the chat is not a paste into the song
  const onClip = (e) => fw.host && e.composedPath().includes(fw.host) && e.stopImmediatePropagation();
  for (const t of ['paste', 'copy', 'cut']) window.addEventListener(t, onClip, true);
  return fw;
}

/** Prerendered pages (speculation rules) wait until the visitor actually arrives. */
function whenActive(fn) {
  if (document.prerendering) document.addEventListener('prerenderingchange', fn, { once: true });
  else fn();
}

function whenBody(fn) {
  if (document.body) fn();
  else document.addEventListener('DOMContentLoaded', fn, { once: true });
}

(() => {
  if (window.__bcAgent) return; // included twice
  const script = findScript();
  const config = readConfig(script);
  const entry = config.site ? siteById(config.site) : siteForUrl(location.href);
  const api = { version: VERSION, site: entry?.id ?? null, config };
  window.__bcAgent = api;
  if (!entry) {
    if (config.site !== 'none') console.info(`[bc-agent] No site adapter for ${config.site ? `data-site="${config.site}"` : location.origin}; the chat bar is off.`);
    return;
  }
  runEarlyHooks(entry);
  const firewall = installFirewall();

  // Chunks are classic scripts that each define one global (build.mjs), so a static host needs
  // no CORS headers for them, as it would for ES modules.
  const loading = new Map();
  const chunk = (name) => {
    const file = CHUNKS[name];
    if (!file) return Promise.reject(new Error(`missing chunk ${name}`));
    if (!loading.has(name)) {
      const global = `__bcAgentChunk_${name.replace(/\W/g, '_')}`;
      const p = new Promise((resolve, reject) => {
        if (window[global]) return resolve(window[global]);
        const s = document.createElement('script');
        s.src = new URL(file, config.base).href;
        s.async = true;
        s.onload = () => (window[global] ? resolve(window[global]) : reject(new Error(`${s.src} did not define ${global}`)));
        s.onerror = () => reject(new Error(`could not load ${s.src}`));
        (document.head ?? document.documentElement).appendChild(s);
      });
      p.catch(() => loading.delete(name)); // try again next time
      loading.set(name, p);
    }
    return loading.get(name);
  };
  const loadSite = () => chunk(entry.chunk).then((m) => m[entry.export]);
  const loadScreenshot = () => chunk('screenshot');

  whenActive(() =>
    whenBody(() => {
      try {
        Object.assign(api, mountWidget({ config, entry, loadSite, loadScreenshot, firewall }));
      } catch (e) {
        console.error('[bc-agent] could not start:', e);
      }
    }),
  );
})();
